import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, Camera, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface ImageUploadProps {
  currentImage?: string | null;
  onImageSelect: (file: File) => void;
  onImageRemove?: () => void;
  showRemoveButton?: boolean;
  loading?: boolean;
  error?: string | null;
  className?: string;
  label?: string;
  description?: string;
  accept?: string;
  maxSizeInMB?: number;
  allowedFormatsText?: string;
}

export const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImage,
  onImageSelect,
  onImageRemove: _onImageRemove, // Intentionally unused - remove API not available
  showRemoveButton = false, // Disabled by default - only replace API available
  loading = false,
  error,
  className,
  label = "Profile Image",
  description = "Upload a profile image for your business",
  accept = "image/*",
  maxSizeInMB = 5,
  allowedFormatsText = "PNG, JPG, GIF",
}) => {
  // Note: showRemoveButton is intentionally not used because we only have a replace API,
  // not a remove/delete API. This prop exists for future compatibility if needed.
  void showRemoveButton;
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (file: File) => {
    // Validate file type
    if (!file.type.startsWith("image/")) {
      return;
    }

    // Validate file size
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      return;
    }

    // Create preview URL
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    // Call parent callback
    onImageSelect(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const displayImage = previewUrl || currentImage;
  const hasImage = Boolean(displayImage);

  return (
    <div className={cn("space-y-4", className)}>
      <div className="space-y-2">
        <Label>{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      <div className="space-y-4">
        {/* Image Preview */}
        {hasImage && (
          <div className="relative inline-block">
            <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-200">
              <img
                src={displayImage || ""}
                alt="Profile preview"
                className="w-full h-full object-cover"
              />
            </div>
            {loading && (
              <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              </div>
            )}
          </div>
        )}

        {/* Upload Area */}
        <div
          className={cn(
            "border-2 border-dashed rounded-lg p-6 text-center transition-colors",
            dragActive
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20"
              : "border-gray-300 hover:border-gray-400",
            hasImage ? "border-gray-200" : ""
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="space-y-3">
            <div className="flex justify-center">
              <div className="p-3 bg-gray-100 dark:bg-gray-800 rounded-full">
                <Camera className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
            </div>

            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {hasImage ? "Change image" : "Upload an image"}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Drag and drop your image here, or click to browse
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                {allowedFormatsText} up to {maxSizeInMB}MB
              </p>
            </div>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleButtonClick}
              disabled={loading}
              className="mt-3"
            >
              <Upload className="h-4 w-4 mr-2" />
              {hasImage ? "Change Image" : "Select Image"}
            </Button>
          </div>
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
          disabled={loading}
        />

        {/* Error Message */}
        {error && (
          <Alert variant="destructive" className="border-red-200 bg-red-50">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
};
